import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { VNG_DIALOG_DATA, VngDialogRef, VngSelectOption, VngTourService } from "@vengreso/angular-components";
import { NgbPopoverConfig } from "@ng-bootstrap/ng-bootstrap";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { distinctUntilChanged } from "rxjs/operators";

import { ApplicationConstants } from "src/app/core/core-services/constants/application.constants";
import {
  PlansConstants,
  PlansRestrictions,
  RewardsConstants,
  getPlanId,
  getRewards,
} from "src/app/core/core-services/constants/plans.constants";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { ExtensionService } from "src/app/core/core-services/services/extension.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { ShortcutService } from "src/app/core/core-services/services/shortcut.service";
import { TinyMceConfigService } from "src/app/core/core-services/services/tiny-mce-config.service";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";
import { NotifyService } from "src/app/core/menu/not.service";
import { CategoryRenameComponent } from "../add-shortcut-category/category-rename/category-rename.component";
import { CategorySelectComponent } from "../../shared/category-select/category-select.component";
import {
  ShortcutCategoryService,
  SHORTCUT_CATEGORIES,
} from "src/app/core/core-services/services/shortcut-category.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { DynamicStorage } from "src/app/core/core-services/decorators/dynamic-storage";
import { GlobalHookService } from "src/app/global-hook.service";
import { clearTinyMCEContent } from "src/app/utils/getSortcutContent";
import { tagTourStepButtons } from "src/app/utils/tour-heap.util";
import { OnboardingProgressService } from "src/app/core/core-services/services/onboarding-progress.service";
import { Store } from "@ngrx/store";
import { selectPlanFeatures } from "src/app/core/store/plan/plan.selectors";
import { take, filter } from "rxjs/operators";

@Component({
    selector: "app-add-shortcut",
    templateUrl: "./add-shortcut.component.html",
    styleUrls: ["./add-shortcut.component.scss"],
    standalone: false
})
export class AddShortcutComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  @DynamicStorage(SHORTCUT_CATEGORIES) categoryList: any[];
  // R5: mat-select + the old select-search plugin -> vng-select [searchable]="true".
  // categoryOptions is the flattened, data-driven option list (built once per
  // categoryList change) — vng-select's own built-in substring search now
  // does the filtering that filterCtrl/filterCategories() used to do by hand.
  categoryOptions: VngSelectOption[] = [];
  editorConfig: any = null; // Initialize as null to prevent undefined errors
  editorApiKey: string;
  editorManager: any;
  isEditorLoaded: boolean;
  shortcutErrors: any[];
  @ViewChild("categorySelect") categorySelect: CategorySelectComponent;

  shortcutIdToBeEdited;
  step: string;

  constructor(
    private readonly fb: FormBuilder,
    config: NgbPopoverConfig,
    public notifyService: NotifyService,
    private readonly dataService: DataService,
    private readonly shortcutService: ShortcutService,
    private readonly notification: NotificationService,
    private readonly extensionService: ExtensionService,
    @Inject(VNG_DIALOG_DATA) public data: any,
    public dialogRef: VngDialogRef<AddShortcutComponent>,
    public dialog: DialogService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly location: Location,
    private readonly sharedData: SharedDataService,
    private readonly shortcutCategoryService: ShortcutCategoryService,
    private readonly tourService: VngTourService,
    private readonly globalHookService: GlobalHookService,
    private tinyMceConfigService: TinyMceConfigService,
    private store: Store,
    private readonly onboardingProgress: OnboardingProgressService
  ) {
    // Issue #541 (stakeholder validation on #521/#533): the 4-step
    // field-by-field walkthrough had already collapsed into ONE tooltip
    // anchored over the whole modal (`vngTourAnchor="modal"` on the <form>
    // itself in the template, spanning the fields AND the Cancel/Save row).
    // Being the tour's only step, it was simultaneously first and last, so
    // vng-tour rendered just a single dismiss button labeled "Next" even
    // though clicking it closed the popup — there was no next. Stakeholder
    // feedback: keep the whole-modal anchor (no field-pinned arrow) but make
    // it a real TWO-step tour — step 1 keeps the verbatim copy and a truthful
    // "Next" that actually advances; step 2 is a save prompt ending with
    // "Got it". Both steps share the same anchor, so `stepId` (not
    // `anchorId`) is what distinguishes them for heap tagging below.
    //
    // QA F-2 on PR #533: `enableBackdrop: true` dims/pointer-blocks
    // everything OUTSIDE the anchor's rect. Even with the anchor widened to
    // include Cancel/Save (see the template), relying on the backdrop's
    // cutout landing pixel-exact on that rect is fragile — `enableBackdrop:
    // false` removes the dimming layer entirely, so there is nothing that can
    // ever intercept a click on a control the anchor legitimately covers.
    // Kept `false` on BOTH steps so Save/Cancel stay clickable throughout.
    //
    // QA F-8 (round 3): the default `{after, below}` placement rendered the
    // popover comfortably clear of Cancel/Save at 1500x980, but at 1280x720
    // (playwright.config.ts's own viewport, a common laptop size) there isn't
    // enough room below the ~565px-tall dialog for the ~150-200px popover, so
    // it gets clamped upward — landing its left-flush card over Cancel (the
    // left button) while Save (further right, outside the card's ~320px
    // width) stays clear. `{after, above}` moves the popover's un-clamped
    // position to ABOVE the dialog instead: if there's ever too little room
    // there either, clamping pulls it DOWN toward the anchor's TOP edge (the
    // title/category-select area), never toward the bottom-anchored button
    // row — the opposite direction from Cancel/Save either way. Kept on BOTH
    // steps for the same reason.
    //
    // QA F-1 on PR #544: `fms-tour-arrow--bottom-start` draws its caret at a
    // FIXED offset from the popover's own top-left corner (`bottom:-8px;
    // left:20px`, app.scss), not relative to whatever the anchor "means" —
    // with a whole-modal anchor that fixed offset happens to land the caret
    // on the Category control every time (measured identical at 1280x720 and
    // 1500x980). The issue is explicit: "highlights the entire modal (no
    // field-specific arrow)". Since the anchor is the whole modal, no single
    // point on its edge is a meaningful arrow target — dropped `popoverClass`
    // entirely on both steps rather than repoint it at another arbitrary
    // spot.
    this.tourService.initialize([
      {
        anchorId: "modal",
        stepId: "step1",
        content:
          'Title and description is used to describe this snippet (aka FlyCut) of text and how to use it. It is for your personal memory and use. If you do not like this title and description simply change it now or click "Next".',
        title: "Describe your FlyCut",
        enableBackdrop: false,
        nextBtnTitle: "Next",
        placement: { xPosition: "after", yPosition: "above" },
      },
      {
        // TODO(product): placeholder copy — flagged for a product tweak, see PR.
        anchorId: "modal",
        stepId: "step2",
        content: "After reviewing your FlyCut, click Save to start using it.",
        title: "Save your FlyCut",
        enableBackdrop: false,
        endBtnTitle: "Got it",
        placement: { xPosition: "after", yPosition: "above" },
      },
    ]);

    this.tourService.stepShow$.subscribe((step) => {
      setTimeout(() => {
        switch (step.stepId) {
          case "step1":
            tagTourStepButtons("walk-me-modal-add-flycut-step-1");
            break;
          case "step2":
            tagTourStepButtons("walk-me-modal-add-flycut-step-2");
            break;
        }
      }, 200);

      this.step = step.anchorId;
    });

    if (this.isOnboarding) {
      setTimeout(() => {
        this.tourService.start();
      }, 500);
    }
    config.triggers = "hover";
    config.container = "body";
  }

  get isOnboarding() {
    return this.globalHookService.onboardingActive;
  }

  async ngOnInit() {
    this.editorApiKey = ApplicationConstants.TINYMCE_API_KEY;

    // Fetch features and generate config
    this.store.select(selectPlanFeatures).pipe(
      filter(features => !!features),
      take(1)
    ).subscribe(features => {
      const baseConfig = this.shortcutService.getEditorConfig();
      const planId = getPlanId();

      this.editorConfig = this.tinyMceConfigService.getPlanBasedConfig(
        features || {} as any, // Fallback if features not loaded yet, though guarded by take(1) usually
        baseConfig,
        this,
        planId,
        (type, error, showTheStarter) => {
          this.dialog.openDialogComponent(
            WarningDialogComponent,
            {
              self: this,
              type: 'editor',
              showTheStarter: showTheStarter,
              error: error,
              isCustomMessage: true
            },
            "800px"
          );
        }
      );

      // Keep the original init callback logic if needed, but handled inside service partially
      // We need to preserve the keyup listener which is specific to this component logic
      const originalInit = this.editorConfig.init_instance_callback;
      this.editorConfig.init_instance_callback = (editor) => {
        if (originalInit) originalInit(editor);
        editor.on("keyup", (e) => {
          // this.isValidateShortcut(); // validation now largely handled by restrictions, but maybe length check remains?
          // keeping length check logic if it persists outside isValidateShortcut
        });
      };
    });


    this.form = this.fb.group({
      title: [null, [Validators.required]],
      shortcut: [null, [Validators.required]],
      category_id: [null, [Validators.required]],
      uploads: [[]],
      html: [null, [Validators.required]],
    });

    if (this.data?.action === "edit") {
      this.prefillEditShortcutForm(this.data.shortcut);
      this.shortcutIdToBeEdited = this.data.shortcut.id;
    }

    this.getCategories();

    this.sharedData.storage
      .observe(SHORTCUT_CATEGORIES)
      .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
      .subscribe((value) => {
        this.categoryList = value;
        this.setCategoryData();
      });

    this.setCategoryData();

    if (this.data?.action !== "edit") {
      this.location.replaceState("flyboard/add-new-flycut");
    }

    if (this.dialogRef.componentInstance.data.shortcutData) {
      const hasData = Object.keys(this.dialogRef.componentInstance.data.shortcutData).length > 0; // Check for empty object

      if (hasData) {
        const title = this.dialogRef.componentInstance.data.shortcutData.title || "";
        const shortcut = this.dialogRef.componentInstance.data.shortcutData.flyCut || "";
        const html = this.dialogRef.componentInstance.data.shortcutData.flyMsg || "";

        this.form.patchValue({
          title,
          shortcut,
          html,
        });
      }
    }
  }

  ngOnDestroy(): void {
    if (this.data?.action === "edit") {
      return;
    } else {
      if (this.data?.parentPage === "flyboard") {
        this.location.replaceState(`flyboard`);
        return;
      } else if (this.data?.parentPage?.includes("shortcuts")) {
        this.location.replaceState(`${this.data?.parentPage}`);
        return;
      }
      this.location.replaceState(`flyboard`);
    }
  }

  prefillEditShortcutForm(shortcut) {
    this.form.patchValue({
      title: shortcut.title,
      shortcut: shortcut.shortcut,
      category_id: shortcut.category_id,
      uploads: shortcut.uploads,
      html: shortcut.html,
    });
  }

  isValidateShortcut() {
    // Legacy validation logic removed. 
    // Character count validation is handled in saveShortcut or could be moved to a validator.
    // Feature restrictions are now applied at the editor configuration level.
    return true;
  }

  flycutExistsSearch(value: any): any {
    this.dataService.flycutExistsSearch({ q: value }).subscribe((response: any) => {
      if (response && response.result) {
        if (response.result.data.exists) {
          this.form.controls.shortcut.setErrors({ notUnique: true });
        }
      }
    });
  }

  // R5: flatten the category tree into data-driven vng-select options once
  // per categoryList change. Indentation uses NBSP (\u00A0 escapes below) —
  // plain spaces collapse in rendered HTML text, NBSP does not.
  buildCategoryOptions(): void {
    const options: VngSelectOption[] = [];
    for (const cat of this.categoryList || []) {
      options.push({ value: cat.id, label: cat.name });
      for (const sub1 of cat.sub_category_lv1 || []) {
        options.push({ value: sub1.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0" + sub1.name });
        for (const sub2 of sub1.sub_category_lv2 || []) {
          options.push({ value: sub2.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0" + sub2.name });
        }
      }
    }
    this.categoryOptions = options;
  }

  saveCategory(data) {
    this.dataService.post({ url: "v1/user/shortcut-category", data }).subscribe(
      (response: any) => {
        if (response && response.result) {
          this.extensionService.sendUpdateMessage();
          this.getCategories(true);
          this.data.self.categoryId = response.result;
          // close the dropdown
          this.categorySelect.closePanel();
          this.notification.toast("Category added successfully!");
        }
      },
      () => { }
    );
  }

  shortcutCategoryRename(data: any) {
    this.dialog.openDialogComponent(CategoryRenameComponent, {
      self: this,
      data,
      backdropClass: "dialog-category-backdrop",
    });
  }

  setEditorManager(event: any) {
    this.isEditorLoaded = true;
    this.editorManager = event.editor.editorManager;
  }

  getCategories(reloadFromApi = false) {
    this.shortcutCategoryService.loadShortcutCategory({ reloadFromApi });
  }

  setCategoryData(): void {
    this.buildCategoryOptions();

    let predefinedCategoryId: string;
    if (this.data.self?.lv2CategoryId) {
      predefinedCategoryId = this.data.self?.lv2CategoryId;
    } else if (this.data.self?.lv1CategoryId) {
      predefinedCategoryId = this.data.self?.lv1CategoryId;
    } else if (this.data.self?.categoryId) {
      predefinedCategoryId = this.data.self?.categoryId;
    }

    this.form.patchValue({
      category_id:
        this.data.self.categoryId && this.data.self.categoryId !== "all"
          ? predefinedCategoryId
          : this.categoryList[0].id,
    });
  }

  getCategoryType(id: string) {
    for (const item of this.categoryList) {
      if (item.id == id) {
        return "category";
      }
      if (item.sub_category_lv1) {
        for (const subLv1 of item.sub_category_lv1) {
          if (subLv1.id == id) {
            return "sub_categories_lv1";
          }
          if (subLv1.sub_category_lv2) {
            for (const subLv2 of subLv1.sub_category_lv2) {
              if (subLv2.id == id) {
                return "sub_categories_lv2";
              }
            }
          }
        }
      }
    }
  }

  // R3: field errors are only painted once a control is dirty/touched, so the
  // submit path has to mark the whole form as touched — otherwise pressing
  // Save on an untouched form would silently do nothing.
  submitForm(): void {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.saveShortcut(this.form.value, "editor");
    }
  }

  saveShortcut(data: any, editorId: string) {
    let planId = getPlanId();

    const editor = this.editorManager.get(editorId);
    this.shortcutService.processMedia(
      editor,
      (uploads: string[]) => {
        const shortcut: any = this.dataService.toSave(data, ["title", "shortcut", "category_id"]);
        shortcut.uploads = uploads;
        shortcut.html = clearTinyMCEContent(editor.getContent());
        shortcut.text = this.shortcutService.getInlineStyledHTML(shortcut.html);
        // Clean text and remove html Tags.
        let leng = shortcut.text.replaceAll("&nbsp;", " ").replace(/<\/?[^>]+(>|$)/g, "").length;

        shortcut.first_line = this.shortcutService.getFirstLine(editor);
        shortcut.type = this.getCategoryType(data.category_id);

        let rewards = getRewards();
        if (!rewards.length && !rewards.includes(RewardsConstants.UNLIMITED_CHARACTERS)) {
          if (planId === "Free" && leng >= PlansConstants.FREE.NUM_OF_CHAR) {
            this.dialog.openDialogComponent(
              WarningDialogComponent,
              {
                self: this,
                type: "numOfChar",
                showTheStarter: planId === "Free",
                error: "maxCharFree",
              },
              "800px"
            );
            return;
          }
          if (planId === "Starter" && leng >= PlansConstants.STARTER.NUM_OF_CHAR) {
            this.dialog.openDialogComponent(
              WarningDialogComponent,
              {
                self: this,
                type: "numOfChar",
                showTheStarter: false,
                error: "maxCharStarter",
              },
              "800px"
            );
            return;
          }
        }

        if (this.data?.action === "edit") {
          this.updateShortcut(shortcut);
        } else {
          this.createShortcut(shortcut);
        }
      },
      data?.uploads
    );
  }

  createShortcut(shortcut) {
    this.dataService.post({ url: "v1/user/shortcut", data: shortcut }).subscribe((response: any) => {
      if (response?.result) {
        this.extensionService.sendUpdateMessage();
        // this.notification.toast("Shortcut added successfully!");

        // Issue #521: a REAL FlyCut save during onboarding is what completes
        // "add_flycut_signature" — never the earlier "Try it out" click (see
        // add-flycut-signature-step.component.ts). legacyStep 3 = WELCOME_MODAL_STEP_ORDER[3]
        // ("test_flycut"), the next onboarding panel.
        if (this.isOnboarding && !this.onboardingProgress.isCompleted("add_flycut_signature")) {
          this.onboardingProgress.completeStep("add_flycut_signature", 3, this.onboardingProgress.maxStep + 1).subscribe();
        }
        // A real save advances the walk-me modal to the NEXT step (3); Cancel
        // (close()'s own default) returns to THIS step (2) so an abandoned
        // attempt can be retried instead of silently skipped.
        this.close({ success: true, result: response }, 3);
        // Issue #541 (QA F-2): the sidebar's FlyCuts submenu already refreshes
        // on every save via close() -> notifyService.notifyAboutChange() ->
        // menu.component.ts's subjectNotifier subscription, which was already
        // true on the base branch — an extra loadShortcutCategory() call here
        // would just duplicate that GET. What never refreshed is FlyBoard's
        // own "My FlyCuts n/20" panel (DashboardComponent.shortcuts /
        // .statistics), because getShortcutList() was only ever invoked via
        // the `isDashboard` dialog-data flag — false when this dialog is
        // opened from onboarding's signature step, so FlyBoard sat behind the
        // walk-me modal showing stale/empty data. DashboardComponent
        // registers itself as `globalHookService.components.dashboard`
        // whenever it's mounted (it stays mounted under this dialog on both
        // launch paths), so calling through that reference refreshes FlyBoard
        // unconditionally — getShortcutList() itself also re-runs
        // getStatistic(), which is what updates the "n/20" counter.
        this.globalHookService.components.dashboard?.getShortcutList();
        //navigate to category page
        if (!this.globalHookService.onboardingActive) {
          this.router.navigate([`/shortcuts/${shortcut.category_id}`]);
        }
      }
      this.tourService.end();
    });
  }

  updateShortcut(shortcut) {
    this.dataService
      .put({
        url: "v1/user/shortcut/" + this.shortcutIdToBeEdited,
        data: shortcut,
        isLoader: false,
      })
      .subscribe(
        (response: any) => {
          if (response) {
            this.extensionService.sendUpdateMessage();
            this.notification.toast("Shortcut updated successfully!");
            let updatedshortcut = {
              ...this.data.shortcut,
              title: shortcut.title,
              shortcut: shortcut.shortcut,
              category_id: shortcut.category_id,
              uploads: shortcut.uploads,
              html: shortcut.html,
              text: this.shortcutService.getInlineStyledHTML(shortcut.html),
              first_line: shortcut.first_line,
            };
            this.dialogRef.close({
              success: true,
              action: "edit",
              result: updatedshortcut,
            });
            this.tourService.end();
          }
        },
        (err) => {
          if (err.status == 403) {
          }
        }
      );
  }

  /**
   * @param nextStep Walk-me panel to reopen when closing during onboarding.
   * Defaults to 2 ("add_flycut_signature" itself — Cancel returns the user to
   * the SAME step so they can retry). `createShortcut()`'s success path
   * passes 3 ("test_flycut") explicitly since a real save should advance,
   * not just reopen.
   */
  close(data?: any, nextStep = 2): void {
    this.notifyService.notifyAboutChange();
    this.dialogRef.close(data);
    if (this.isOnboarding) {
      this.globalHookService.components.global.startModal(nextStep);
    }
    this.tourService.end();
  }
}
